import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { useStorage } from '~Components/Storage';
import Toggle from '~Components/Toggle';
import Icon from '~ComponentsImproved/Icon';
import { useBridgeApi, useButton, useDeviceInfo, useDispatch, useLogger } from '~Hooks';
import { BaseInputStyling, Box, Button } from '~Styles';
import qrIcon from '~Assets/icons/qr-scan.svg';
import manualIcon from '~Assets/icons/settings.svg';
import useI18n from '~Translations';
import { handyBreakpoint } from '~Styles/breakpoints';
import Modal from '~ComponentsImproved/Modal';
import ScanInfo from './ScanInfo';
import * as actions from '~actions';

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin: 16px 0;
  gap: 16px;
`;

const sharedButtonStyles = css`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-radius: 4px;
  height: 150px;
  width: 50%;
  font-size: 20px;
  font-weight: 600;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  box-sizing: border-box;
  transition: transform 0.1s ease-out;

  ${handyBreakpoint} {
    width: 100%;
    height: 100px;
    font-size: 14px;

    & > div {
      display: none;
    }
  }
`;

const ScanButton = styled.div<{ touching?: boolean }>`
  ${sharedButtonStyles};
  background-color: var(--colorUIBlue);
  color: var(--colorWhite);
  border: none;
  outline: none;
  transform: ${({ touching }) => (touching ? 'scale(0.98)' : '1')};
`;

const ManualButton = styled.div<{ touching?: boolean }>`
  ${sharedButtonStyles};
  background-color: var(--colorWhite);
  border: 1px solid var(--colorBlueGray300);
  color: var(--colorUIBlue);
  transform: ${({ touching }) => (touching ? 'scale(0.98)' : '1')};
`;

const MiniScanButton = styled.div<{ touching?: boolean }>`
  width: 64px;
  border-radius: 4px;
  background-color: var(--colorWhite);
  color: var(--colorUIBlue);
  border: 1px solid var(--colorBlueGray200);
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  outline: none;
  transform: ${({ touching }) => (touching ? 'scale(0.98)' : '1')};
`;

const AddTerminalWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
`;

const TerminalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  text-align: left;
`;

const AddTerminalInput = styled.input`
  ${BaseInputStyling}
  padding: 16px 8px;
  margin: 4px 0 16px;
`;

const AddTerminalButton = styled(Button)<{ disabled: boolean }>`
  color: white;
  flex: 1;
  text-align: center;
  :active {
    opacity: 0.8;
  }
  opacity: ${({ disabled }) => (disabled ? '0.75 !important' : 1)};
  background-color: var(--colorUIBlue);
`;

const Error = styled.div`
  color: red;
`;
interface Props {
  useCloud?: boolean;
  onComplete?: () => void;
}

const AddNew: FC<Props> = ({ useCloud = false, onComplete }) => {
  const logger = useLogger('adyen-settings');
  const { i18n } = useI18n();
  const [storage, updateStorage] = useStorage();
  const [error, setError] = useState('');
  const [IP, setIP] = useState('');
  const [ID, setID] = useState('');
  const [hasPrinter, setHasPrinter] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showScanInfo, setShowScanInfo] = useState(false);
  const bridgeApi = useBridgeApi();
  const dispatch = useDispatch();
  const deviceInfo = useDeviceInfo();
  const adyenTerminals = storage.adyenTerminals;

  const scanButton = useButton({
    onClick: () => setShowScanInfo(true),
  });
  const manualButton = useButton({
    onClick: () => setShowManual(true),
  });

  useEffect(() => {
    setIP('');
    setID('');
    setHasPrinter(false);
    setError('');
  }, [adyenTerminals.length]);

  // All Adyen terminals with an ID starting with 'V' should support printing from terminal
  const terminalIdHasPrinter = (id: string) => !id.toLowerCase().startsWith('p');

  useEffect(() => {
    if (terminalIdHasPrinter(ID)) {
      setHasPrinter(true);
    } else {
      setHasPrinter(false);
    }
  }, [ID]);

  const handleAdd = () => {
    if (!ID) {
      return;
    }

    setError('');

    if (adyenTerminals.find((terminal) => !!IP.length && terminal.IP === IP)) {
      return setError('Terminal with this IP-Address is already added');
    }

    // This check only applies to non-cloud terminals (manual)
    if (
      adyenTerminals.find(
        (terminal) => !!terminal.IP && terminal.POIID === ID && terminal.IP === IP,
      )
    ) {
      return setError('Terminal with this ID is already added');
    }

    // Filter away the previous terminal with the same ID
    const filteredTerminals = adyenTerminals.filter((at) => at.POIID !== ID);

    updateStorage({
      adyenTerminals: [
        ...filteredTerminals,
        {
          IP: IP,
          POIID: ID,
          hasPrinter,
        },
      ],
    });
    dispatch(actions.addToast(i18n('Settings.AddTerminal.Completed'), true));
    onComplete && onComplete();
  };

  function handleScan(data: string | null) {
    if (!data) {
      return;
    }

    logger.info('Scan Result: ' + data);

    try {
      const terminal = JSON.parse(data);
      logger.info('Parsed results', terminal);

      const ip = terminal.ip || terminal.ifs.ip || terminal.ifs.eth?.ip || terminal.ifs.wifi?.ip;

      // Filter away the previous terminal with the same ID
      const filteredTerminals = adyenTerminals.filter((at) => at.POIID !== terminal.terminal);
      updateStorage({
        adyenTerminals: [
          ...filteredTerminals,
          {
            IP: ip ?? null,
            POIID: terminal.terminal,
            hasPrinter: terminalIdHasPrinter(terminal.terminal),
          },
        ],
      });
      dispatch(actions.addToast(i18n('Settings.AddTerminal.Completed'), true));
      onComplete && onComplete();
    } catch (e) {
      setError('Invalid scan');
      logger.error((e as Error).message);
    }
  }

  return (
    <AddTerminalWrapper>
      {showManual ? (
        <div>
          <TerminalContent>
            <div>{i18n('Settings.TerminalIPAddress')}</div>
            <AddTerminalInput
              value={IP}
              placeholder={'e.g. 192.168.1.1'}
              onChange={(e) => setIP(e.target.value)}
            />
            <div>
              {i18n('Settings.TerminalID')} <i>{i18n('Settings.AddNewTerminal.text')}</i>
            </div>
            <AddTerminalInput
              value={ID}
              placeholder={'e.g. P400Plus-123456789'}
              onChange={(e) => setID(e.target.value)}
            />
            <Box marginBottom="small">
              <Toggle
                label={i18n('Settings.HasBuiltInPrinter')}
                subLabel={i18n('Settings.HasBuiltInPrinter.Text')}
                value={hasPrinter}
                onClick={() => setHasPrinter(!hasPrinter)}
              />
            </Box>
          </TerminalContent>
          <ButtonWrapper>
            {!deviceInfo?.isAdyenTerminal && (
              <MiniScanButton {...scanButton}>
                <Icon src={qrIcon} height="32px" width="32px" color="var(--colorUIBlue)" />
              </MiniScanButton>
            )}
            <AddTerminalButton disabled={!ID} onClick={handleAdd}>
              {i18n('Settings.Add')}
            </AddTerminalButton>
          </ButtonWrapper>
        </div>
      ) : (
        <ButtonWrapper>
          {!deviceInfo?.isAdyenTerminal && (
            <ScanButton {...scanButton}>
              {i18n('Settings.AddTerminalScan')}
              <Icon src={qrIcon} height="40px" width="40px" />
            </ScanButton>
          )}
          <ManualButton {...manualButton}>
            {i18n('Settings.AddTerminalManually')}
            <Icon src={manualIcon} height="40px" width="40px" color="var(--colorUIBlue)" />
          </ManualButton>
        </ButtonWrapper>
      )}

      <Modal isShowing={showScanInfo} backdropClose hide={() => setShowScanInfo(false)}>
        <ScanInfo
          startScan={() => {
            setShowScanInfo(false);
            bridgeApi.scanQRCode().then(handleScan);
          }}
        />
      </Modal>

      {error && <Error>{error}</Error>}
    </AddTerminalWrapper>
  );
};

export default AddNew;
